import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import type { RouterContextWithSession } from "../../../router";
import {
  listWebsiteDigestIssues,
  updateWebsiteDigestIssue,
  type WebsiteDigestIssueRow,
} from "../../../server/website-digest";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Textarea } from "../../../components/ui/textarea";
import { toast } from "../../../components/ui/sonner";
import { ExternalLink } from "lucide-react";

export const Route = createFileRoute("/_admin/website-digest/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;
    await context.queryClient.ensureQueryData({
      queryKey: ["website-digest-issues"],
      queryFn: () =>
        listWebsiteDigestIssues({
          data: { accessToken: session.access_token },
        }),
    });
  },
  pendingComponent: () => <PageSkeleton rows={4} variant="list" />,
  errorComponent: createRouteError("Website Digest"),
  component: WebsiteDigestAdminPage,
});

function WebsiteDigestAdminPage() {
  const { session, loading, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editorBody, setEditorBody] = useState("");

  const issuesQuery = useQuery({
    queryKey: ["website-digest", "issues"],
    enabled: Boolean(session?.access_token),
    queryFn: async () => {
      const result = await listWebsiteDigestIssues({
        data: { accessToken: session!.access_token },
      });
      if (result.error) throw new Error(result.error);
      return result.data?.issues ?? [];
    },
  });

  const selected = issuesQuery.data?.find((i) => i.id === selectedId) ?? null;

  const saveMutation = useMutation({
    mutationFn: async (input: {
      issueId: string;
      markdownBody?: string;
      status?: "pending_review" | "published" | "rejected";
    }) => {
      const result = await updateWebsiteDigestIssue({
        data: {
          accessToken: session!.access_token,
          issueId: input.issueId,
          markdownBody: input.markdownBody,
          status: input.status,
        },
      });
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["website-digest"] });
      toast.success("Digest issue updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (loading || !isAuthenticated || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-6xl">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Website Digest</h1>
        <p className="text-muted-foreground mt-1 max-w-2xl">
          Review agent drafts before they go live on the marketing site /digest route. Publishing is
          manual — there is no auto-publish path.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Issues</CardTitle>
            <CardDescription>Draft and pending issues from the ingest pipeline.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {issuesQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (issuesQuery.data?.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">
                No issues yet. Use the <code className="text-xs">website-digest-pipeline</code> edge
                function or insert rows after migration.
              </p>
            ) : (
              issuesQuery.data?.map((issue: WebsiteDigestIssueRow) => (
                <button
                  key={issue.id}
                  type="button"
                  onClick={() => {
                    setSelectedId(issue.id);
                    setEditorBody(issue.markdown_body ?? "");
                  }}
                  className={`w-full text-left rounded-md border p-3 transition-colors ${
                    selectedId === issue.id ? "border-primary bg-muted/40" : "border-border"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm">{issue.title}</span>
                    <StatusBadge status={issue.status} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {issue.slug} · {issue.week_start} – {issue.week_end}
                  </p>
                </button>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Review</CardTitle>
            <CardDescription>
              Edit markdown, then approve to publish. Public URL uses slug{" "}
              <code className="text-xs">/digest/[yyyy-mm-dd]</code> on the marketing site.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!selected ? (
              <p className="text-sm text-muted-foreground">Select an issue to review.</p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {selected.status === "published" ? (
                    <Button variant="outline" size="sm" asChild>
                      <a
                        href={`${getMarketingDigestBaseUrl()}/digest/issue?slug=${encodeURIComponent(selected.slug)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        View live
                      </a>
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={saveMutation.isPending}
                    onClick={() =>
                      saveMutation.mutate({
                        issueId: selected.id,
                        markdownBody: editorBody,
                        status: "pending_review",
                      })
                    }
                  >
                    Save draft
                  </Button>
                  <Button
                    size="sm"
                    disabled={saveMutation.isPending || !editorBody.trim()}
                    onClick={() =>
                      saveMutation.mutate({
                        issueId: selected.id,
                        markdownBody: editorBody,
                        status: "published",
                      })
                    }
                  >
                    Approve & publish
                  </Button>
                </div>
                <Textarea
                  value={editorBody}
                  onChange={(e) => setEditorBody(e.target.value)}
                  rows={18}
                  className="font-mono text-sm"
                  placeholder="Markdown body (FlowState voice, attributed summaries only)…"
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function getMarketingDigestBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_MARKETING_SITE_URL as string | undefined;
  return (fromEnv?.trim() || "").replace(/\/$/, "");
}

function StatusBadge({ status }: { status: string }) {
  const variant =
    status === "published"
      ? "default"
      : status === "pending_review"
        ? "secondary"
        : "outline";
  return <Badge variant={variant}>{status}</Badge>;
}
